import * as R from 'ramda';
import {
  pure,
  compose,
  withState,
  withHandlers,
  withPropsOnChange,
} from 'react-recompose';
// components
import {
  REPORT_FILTER_DATE_LAST_NEXT_OPERATIONS,
  REPORT_FILTER_DATE_OPERATION_SPECIFIC_DAY,
} from '../../../filter/settings';
// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
// hocs
import { withComponentDidUpdatePropCallback } from '../../../../hocs';
// feature edit-report
import { rangeObject, defaultFilterFields } from './settings';
//////////////////////////////////////////////////

export const withReportFilterList = R.compose(
  withState('reportFilterGroups', 'setReportFilterGroups', []),
  withState('reportSimpleFiltersGroupId', 'setReportSimpleFiltersGroupId', null),
  withPropsOnChange(['filterFields'], (props) => {
    const { filterFields, setReportFilterGroups, setReportSimpleFiltersGroupId } = props;

    const defaultGroupId = G.generateGuid();
    setReportSimpleFiltersGroupId(defaultGroupId);

    if (G.isNilOrEmpty(filterFields)) return;

    const groups = R.compose(
      R.groupBy(R.prop('groupId')),
      R.map((item) => {
        const groupId = R.prop('groupId', item);

        if (G.isNilOrEmpty(groupId)) {
          return {
            ...item,
            withoutOrGroup: true,
            groupId: defaultGroupId,
          };
        }

        return item;
      }),
    )(filterFields);
    setReportFilterGroups(groups);
  }),
  withHandlers({
    handleAddFiltersGroup: (props) => () => {
      const { reportFilterGroups, setReportFilterGroups, reportSimpleFiltersGroupId } = props;

      const groupId = reportSimpleFiltersGroupId;
      const filter = {
        ...defaultFilterFields,
        groupId,
        withoutOrGroup: true,
      };
      const newGroups = R.assoc(groupId, R.of(Array, filter), reportFilterGroups);
      setReportFilterGroups(newGroups);
    },
    handleAddReportGroup: (props) => () => {
      const { reportFilterGroups, setReportFilterGroups } = props;

      const groupId = G.generateGuid();
      const filter = R.assoc('groupId', groupId, defaultFilterFields);
      const newGroups = R.assoc(groupId, R.of(Array, filter), reportFilterGroups);
      setReportFilterGroups(newGroups);
    },
    handleAddReportFilter: (props) => ({ groupId, withoutOrGroup }) => {
      const { reportFilterGroups, setReportFilterGroups } = props;

      const group = R.path([groupId], reportFilterGroups);
      const filter = {
        ...defaultFilterFields,
        groupId,
        withoutOrGroup,
      };
      const newGroups = R.assoc(groupId, R.append(filter, group), reportFilterGroups);
      setReportFilterGroups(newGroups);
    },
    handleRemoveReportFilter: (props) => (groupId, filterIndex) => {
      const { reportFilterGroups, setReportFilterGroups } = props;

      const groupsUpdated = R.dissocPath([groupId, filterIndex], reportFilterGroups);
      const group = R.path([groupId], groupsUpdated);

      if (G.isNilOrEmpty(group)) {
        return setReportFilterGroups(R.dissoc(groupId, reportFilterGroups));
      }

      return setReportFilterGroups(groupsUpdated);
    },
    handleSelectPropertyName: (props) => (values) => {
      const { reportFilterGroups, setReportFilterGroups } = props;

      const {
        groupId,
        filterIndex,
        withoutOrGroup,
        data: { type, value, uiType, collection, referenceFieldName },
      } = values;

      let dataType = R.or(uiType, type);

      if (G.isNilOrEmpty(dataType)) {
        const fieldData = G.getFieldTypeAndSearchable(value);

        dataType = R.pathOr('string', [GC.FIELD_TYPE], fieldData);
      }

      let newFilter = {
        ...defaultFilterFields,
        groupId,
        dataType,
        withoutOrGroup,
        propertyName: value,
        collection: R.or(collection, false),
        allowEdit: R.pathOr(true, [groupId, filterIndex, 'allowEdit'], reportFilterGroups),
      };

      if (R.equals(type, 'reference')) {
        newFilter = {
          ...newFilter,
          referenceName: value,
          propertyName: 'references',
        };

        if (G.isNotNil(referenceFieldName)) {
          newFilter = R.assoc(GC.FIELD_REFERENCE_FIELD_NAME, referenceFieldName, newFilter);
        }
      }

      const groupsUpdated = R.assocPath([groupId, filterIndex], newFilter, reportFilterGroups);
      setReportFilterGroups(groupsUpdated);
    },
    handleSelectOperation: (props) => (values) => {
      const { reportFilterGroups, setReportFilterGroups } = props;

      const { groupId, dataType, filterIndex, data: { value } } = values;

      const groupsUpdated = R.assocPath([groupId, filterIndex, 'operation'], value, reportFilterGroups);

      if (R.equals(dataType, 'dateOnly')) {
        let groups = R.compose(
          R.assocPath([groupId, filterIndex, 'timeUnit'], null),
          R.assocPath([groupId, filterIndex, 'dateRelative'], false),
        )(groupsUpdated);

        if (R.equals(value, 'equal')) {
          const from = R.path([groupId, filterIndex, 'from'], groups);

          groups = R.assocPath([groupId, filterIndex, 'to'], from, groups);
        }

        return setReportFilterGroups(groups);
      }

      if (R.equals(dataType, 'date')) {
        const dateRelative = R.includes(value, REPORT_FILTER_DATE_LAST_NEXT_OPERATIONS);

        if (G.isTrue(dateRelative)) {
          const groups = R.compose(
            R.assocPath([groupId, filterIndex, 'timeUnit'], value),
            R.assocPath([groupId, filterIndex, 'operation'], null),
            R.assocPath([groupId, filterIndex, 'dateRelative'], dateRelative),
          )(groupsUpdated);

          return setReportFilterGroups(groups);
        }

        if (R.equals(value, REPORT_FILTER_DATE_OPERATION_SPECIFIC_DAY)) {
          const groups = R.compose(
            R.assocPath([groupId, filterIndex, 'timeUnit'], value),
            R.assocPath([groupId, filterIndex, 'operation'], 'equal'),
            R.assocPath([groupId, filterIndex, 'dateRelative'], false),
          )(groupsUpdated);

          return setReportFilterGroups(groups);
        }

        const groups = R.compose(
          R.assocPath([groupId, filterIndex, 'timeUnit'], null),
          R.assocPath([groupId, filterIndex, 'dateRelative'], false),
        )(groupsUpdated);

        return setReportFilterGroups(groups);
      }

      setReportFilterGroups(groupsUpdated);
    },
    handleChangeSelect: (props) => ({ values, filterProp }) => {
      const { reportFilterGroups, setReportFilterGroups } = props;

      const { groupId, filterIndex, data: { value } } = values;

      const groupsUpdated = R.assocPath([groupId, filterIndex, filterProp], value, reportFilterGroups);
      setReportFilterGroups(groupsUpdated);
    },
    handleChangeInput: (props) => ({ value, groupId, filterProp, filterIndex }) => {
      const { reportFilterGroups, setReportFilterGroups } = props;

      const groupsUpdated = R.assocPath([groupId, filterIndex, filterProp], value, reportFilterGroups);
      setReportFilterGroups(groupsUpdated);
    },
    handleChangeMultiSelect: (props) => ({ options, groupId, filterIndex }) => {
      const { reportFilterGroups, setReportFilterGroups } = props;

      const groupsUpdated = R.assocPath([groupId, filterIndex, 'selectMultipleValue'], options, reportFilterGroups);
      setReportFilterGroups(groupsUpdated);
    },
    handleChangeRange: (props) => ({ value, groupId, rangeType, filterProp, filterIndex }) => {
      const { reportFilterGroups, setReportFilterGroups } = props;

      const filterRange = R.path([groupId, filterIndex, filterProp], reportFilterGroups);
      let rangeUpdated = G.ifElse(
        G.isNilOrEmpty(filterRange),
        R.assoc(rangeType, value, rangeObject),
        R.assoc(rangeType, value, R.or(filterRange, {})),
      );

      if (R.all(G.isNilOrEmpty, R.values(rangeUpdated))) rangeUpdated = null;

      const groupsUpdated = R.assocPath([groupId, filterIndex, filterProp], rangeUpdated, reportFilterGroups);
      setReportFilterGroups(groupsUpdated);
    },
    handleChangeDate: (props) => ({ value, groupId, rangeType, filterProp, filterIndex }) => {
      const { reportFilterGroups, setReportFilterGroups } = props;

      if (R.equals(filterProp, 'dateRelative')) {
        const groupsUpdated = R.assocPath(
          [groupId, filterIndex, rangeType],
          G.ifElse(R.isEmpty(value), null, G.toNumber(value)),
          reportFilterGroups,
        );

        return setReportFilterGroups(groupsUpdated);
      }

      if (R.equals(filterProp, 'stringValue')) {
        const groupsUpdated = R.assocPath([groupId, filterIndex, filterProp], value, reportFilterGroups);

        return setReportFilterGroups(groupsUpdated);
      }

      const valueToUse = G.ifElse(
        R.equals(value, 'Invalid date'),
        null,
        value,
      );
      let groupsUpdated = R.assocPath([groupId, filterIndex, rangeType], valueToUse, reportFilterGroups);

      const operation = R.path([groupId, filterIndex, 'operation'], reportFilterGroups);
      const shouldSyncDateOnlyRange = G.isAllTrue(
        R.equals(operation, 'equal'),
        R.equals(rangeType, 'from'),
        R.equals(filterProp, 'dateOnlyRange'),
      );

      if (shouldSyncDateOnlyRange) {
        groupsUpdated = R.assocPath([groupId, filterIndex, 'to'], valueToUse, groupsUpdated);
      }

      setReportFilterGroups(groupsUpdated);
    },
  }),
  pure,
);

export const withPageReportFilter = compose(
  withState('filter', 'setFilter', ({ filterData }) => filterData),
  withHandlers({
    handleSetFilter: ({ setFilter }) => (propName, newProps) => (
      setFilter(G.getPropFromObject(propName, newProps))
    ),
    handleRemoveFilter: (props) => (event) => {
      const {
        setUsedReport,
        isQuickFilter,
        selectedReport,
        getItemListRequest,
        setQuickFilterParams,
        getQuickFilteredListRequest,
        filterData: { searchCriteriaIndex },
      } = props;

      if (isQuickFilter) {
        setQuickFilterParams({});
        getQuickFilteredListRequest();
      } else {
        setUsedReport(R.dissocPath(['searchCriteria', searchCriteriaIndex], selectedReport));
        getItemListRequest(true);
      }
    },
    handleSaveFilter: (props) => () => {
      const {
        isNew,
        filter,
        setFilter,
        setUsedReport,
        isQuickFilter,
        selectedReport,
        getItemListRequest,
        setQuickFilterParams,
        getQuickFilteredListRequest,
        filterData: { searchCriteriaIndex },
      } = props;

      const {
        name,
        groupId,
        dataType,
        origValue,
        propertyName,
        withoutOrGroup,
      } = filter;

      if (R.or(G.isNilOrEmpty(propertyName), R.equals(dataType, ''))) {
        return;
        // TODO: add validation
      }

      const groupIdToUse = G.ifElse(G.isTrue(withoutOrGroup), null, groupId);

      const dataToSave = R.head(R.of(Array, {
        ...filter,
        groupId: groupIdToUse,
        [GC.FIELD_NAME]: R.or(origValue, name),
      }));

      if (isQuickFilter) {
        setQuickFilterParams(dataToSave);
        getQuickFilteredListRequest();
      } else {
        const index = G.ifElse(
          isNew,
          R.length(G.getPropFromObject('searchCriteria', selectedReport)),
          searchCriteriaIndex,
        );

        setUsedReport(R.assocPath(['searchCriteria', index], dataToSave, selectedReport));
        getItemListRequest(true);

        setFilter(defaultFilterFields);
      }
    },
    handleSelectPropertyName: (props) => (values) => {
      const { filter, setFilter } = props;

      const {
        groupId,
        withoutOrGroup,
        data: { type, value, uiType, collection, referenceFieldName },
      } = values;

      let newFilter = {
        ...defaultFilterFields,
        groupId,
        withoutOrGroup,
        propertyName: value,
        dataType: R.or(uiType, type),
        collection: R.or(collection, false),
        allowEdit: R.propOr(true, 'allowEdit', filter),
      };

      if (R.equals(type, 'reference')) {
        newFilter = {
          ...newFilter,
          referenceName: value,
          propertyName: 'references',
        };

        if (G.isNotNil(referenceFieldName)) {
          newFilter = R.assoc(GC.FIELD_REFERENCE_FIELD_NAME, referenceFieldName, newFilter);
        }
      }

      setFilter(newFilter);
    },
    handleSelectOperation: (props) => (values) => {
      const { filter, setFilter } = props;

      const { dataType, data: { value } } = values;

      let newFilter = R.assoc('operation', value, filter);

      if (R.equals(dataType, 'dateOnly')) {
        newFilter = {
          ...filter,
          timeUnit: null,
          operation: value,
          dateRelative: false,
          ...R.equals(value, 'equal') ? { to: R.prop('from', filter) } : {},
        };

        return setFilter(newFilter);
      }

      if (R.equals(dataType, 'date')) {
        const dateRelative = R.includes(value, REPORT_FILTER_DATE_LAST_NEXT_OPERATIONS);

        if (G.isTrue(dateRelative)) {
          newFilter = {
            ...filter,
            dateRelative,
            timeUnit: value,
            operation: null,
          };

          return setFilter(newFilter);
        }

        if (R.equals(value, REPORT_FILTER_DATE_OPERATION_SPECIFIC_DAY)) {
          newFilter = {
            ...filter,
            timeUnit: value,
            operation: 'equal',
            dateRelative: false,
          };

          return setFilter(newFilter);
        }

        newFilter = {
          ...filter,
          timeUnit: null,
          operation: value,
          dateRelative: false,
        };

        return setFilter(newFilter);
      }

      setFilter(newFilter);
    },
    handleChangeSelect: (props) => ({ values, filterProp }) => {
      const { filter, setFilter } = props;

      const { data: { value } } = values;

      setFilter(R.assoc(filterProp, value, filter));
    },
    handleChangeInput: (props) => ({ value, filterProp }) => {
      const { filter, setFilter } = props;

      setFilter(R.assoc(filterProp, value, filter));
    },
    handleChangeMultiSelect: (props) => ({ options }) => {
      const { filter, setFilter } = props;

      setFilter(R.assoc('selectMultipleValue', options, filter));
    },
    handleChangeRange: (props) => ({ value, rangeType, filterProp }) => {
      const { filter, setFilter } = props;

      const filterRange = G.getPropFromObject(filterProp, filter);

      let rangeUpdated = G.ifElse(
        G.isNilOrEmpty(filterRange),
        R.assoc(rangeType, value, rangeObject),
        R.assoc(rangeType, value, R.or(filterRange, {})),
      );

      if (R.all(G.isNilOrEmpty, R.values(rangeUpdated))) rangeUpdated = null;

      setFilter(R.assoc(filterProp, rangeUpdated, filter));
    },
    handleChangeDate: (props) => ({ value, rangeType, filterProp }) => {
      const { filter, setFilter } = props;

      if (R.equals(filterProp, 'dateRelative')) {
        setFilter(R.assoc(rangeType, G.ifElse(R.isEmpty(value), null, G.toNumber(value)), filter));
      }

      if (R.equals(filterProp, 'stringValue')) {
        return setFilter(R.assoc(filterProp, value, filter));
      }

      const valueToUse = G.ifElse(
        R.equals(value, 'Invalid date'),
        null,
        value,
      );

      const filterUpdated = R.assoc(rangeType, valueToUse, filter);
      const shouldSyncDateOnlyRange = G.isAllTrue(
        R.equals(R.prop('operation', filter), 'equal'),
        R.equals(rangeType, 'from'),
        R.equals(filterProp, 'dateOnlyRange'),
      );

      setFilter(G.ifElse(
        shouldSyncDateOnlyRange,
        R.assoc('to', valueToUse, filterUpdated),
        filterUpdated,
      ));
    },
  }),
  withComponentDidUpdatePropCallback({
    propName: 'filterData',
    callbackName: 'handleSetFilter',
  }),
  pure,
);
